import { useCallback, useEffect, useMemo, useState } from "react";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";
import {
  ContestGroupType,
  DistributionItemType,
  LotteryOptionType,
  StatusCountsType,
  StatusFilterType,
} from "../types";

const SEARCH_DEBOUNCE_MS = 300;

const STATUS_BY_FILTER: Record<
  Exclude<StatusFilterType, "all">,
  number
> = {
  pending: DISTRIBUTION_PROCESS_STATUS.PENDING,
  accepted: DISTRIBUTION_PROCESS_STATUS.ACCEPTED,
  rejected: DISTRIBUTION_PROCESS_STATUS.REJECTED,
};

/**
 * Agrupa os itens por concurso preservando a ordem em que a API os devolveu
 * (pendentes primeiro).
 *
 * Cópia deliberada da transformação da tela antiga: o módulo não compartilha
 * código com src/App/Distributions/SellersPending.
 */
export const groupByContest = (
  items: DistributionItemType[]
): ContestGroupType[] => {
  const contestsMap = new Map<string, ContestGroupType>();

  items.forEach((item) => {
    if (!contestsMap.has(item.contestId)) {
      contestsMap.set(item.contestId, {
        id: item.contestId,
        name: item.contestName,
        lotteryId: item.lotteryId,
        lotteryName: item.lotteryName,
        lotteryColor: item.lotteryColor,
        items: [],
      });
    }

    contestsMap.get(item.contestId)?.items.push(item);
  });

  return Array.from(contestsMap.values());
};

const matchesSearch = (item: DistributionItemType, term: string) => {
  if (!term) return true;

  return [
    item.name,
    item.classification,
    item.contestName,
    item.sellerName,
  ]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(term));
};

export const useDistributionFilters = (items: DistributionItemType[]) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lotteryFilter, setLotteryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>("all");

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );

    return () => clearTimeout(timeout);
  }, [search]);

  const lotteryOptions = useMemo<LotteryOptionType[]>(() => {
    const map = new Map<number, LotteryOptionType>();

    items.forEach((item) => {
      if (!map.has(item.lotteryId)) {
        map.set(item.lotteryId, {
          id: item.lotteryId,
          name: item.lotteryName,
          color: item.lotteryColor,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "pt-BR")
    );
  }, [items]);

  /** Recorte por busca e loteria — é sobre ele que os contadores das abas são calculados. */
  const searchedItems = useMemo(
    () =>
      items.filter((item) => {
        if (lotteryFilter !== "all" && String(item.lotteryId) !== lotteryFilter) {
          return false;
        }

        return matchesSearch(item, debouncedSearch);
      }),
    [items, lotteryFilter, debouncedSearch]
  );

  const counts = useMemo<StatusCountsType>(
    () => ({
      all: searchedItems.length,
      pending: searchedItems.filter(
        (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING
      ).length,
      accepted: searchedItems.filter(
        (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.ACCEPTED
      ).length,
      rejected: searchedItems.filter(
        (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.REJECTED
      ).length,
    }),
    [searchedItems]
  );

  const filteredItems = useMemo(() => {
    if (statusFilter === "all") return searchedItems;

    const statusId = STATUS_BY_FILTER[statusFilter];

    return searchedItems.filter((item) => item.statusId === statusId);
  }, [searchedItems, statusFilter]);

  const contests = useMemo(() => groupByContest(filteredItems), [filteredItems]);

  const hasActiveFilters =
    search.trim() !== "" || lotteryFilter !== "all" || statusFilter !== "all";

  const clearFilters = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setLotteryFilter("all");
    setStatusFilter("all");
  }, []);

  return {
    search,
    setSearch,
    debouncedSearch,
    lotteryFilter,
    setLotteryFilter,
    statusFilter,
    setStatusFilter,
    lotteryOptions,
    counts,
    filteredItems,
    contests,
    hasActiveFilters,
    clearFilters,
  };
};
